import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import dayjs from "dayjs";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { api } from "../../../../api/apiClient";
import PageHeader from "../../../../app/layout/PageHeader";
import { useAuth } from "../../../../context/AuthContext";
import {
  buildDocumentFileName,
  DocumentActions,
} from "../../../../shared/document-export";
import AppDatePicker from "../../../components-ui/AppDatePicker";
import { ServiceDto, ServiceInstanceDto } from "../../../types/types";
import MultiServiceInstanceRotaBuilder from "./MultiServiceInstanceRotaBuilder";
import {
  generateRotaPdf,
  RotaExportData,
} from "./rotaDocumentService";

import "../../../../css/buttons.css";
import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "./ServiceRotaPage.css";

const MAX_SELECTED_SESSIONS = 5;

interface RouteParams {
  serviceId: string;
}

interface LocationState {
  rotaOrigin?: "dashboard" | "services";
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "2-digit",
  month: "short",
  year: "2-digit",
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

export default function ServiceRotaPage() {
  const { selectedTenant, user } = useAuth();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { serviceId } = useParams<RouteParams>();
  const serviceIdNumber = Number(serviceId);
  const today = useMemo(() => new Date(), []);
  const rotaOrigin = location.state?.rotaOrigin ?? "dashboard";

  const handleBack = () => {
    if (rotaOrigin === "services") {
      history.push(`/admin/manage-services?serviceId=${serviceIdNumber}`);
      return;
    }

    history.push("/dashboard");
  };

  const [service, setService] = useState<ServiceDto | null>(null);
  const [instances, setInstances] = useState<ServiceInstanceDto[]>([]);
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<number[]>([]);
  const [loadedInstances, setLoadedInstances] = useState<ServiceInstanceDto[]>([]);
  const [rotaExportData, setRotaExportData] = useState<RotaExportData[] | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [fromDate, setFromDate] = useState(today.toISOString().substring(0, 10));
  const [toDate, setToDate] = useState(
    new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .substring(0, 10),
  );

  useEffect(() => {
    api<ServiceDto>(`/api/services/${serviceIdNumber}`).then(setService);
  }, [serviceIdNumber]);

  const loadSessions = useCallback(async () => {
    const result = await api<ServiceInstanceDto[]>("/api/ServicesCalendar/search", {
      method: "POST",
      body: {
        serviceIds: [serviceIdNumber],
        fromDate,
        toDate: toDate ? dayjs(toDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS") : toDate,
      },
    });

    const sessions = (result ?? [])
      .filter(
        (instance) =>
          instance.status !== "Cancelled" &&
          (showHistory
            ? new Date(instance.endDateTime) < new Date()
            : new Date(instance.endDateTime) >= new Date()),
      )
      .sort(
        (a, b) =>
          (showHistory ? -1 : 1) *
          (new Date(a.startDateTime).getTime() -
            new Date(b.startDateTime).getTime()),
      );

    setInstances(sessions);

    const availableIds = new Set(
      sessions.map((instance) => instance.serviceInstanceId),
    );
    setSelectedInstanceIds((current) =>
      current.filter((instanceId) => availableIds.has(instanceId)),
    );
  }, [fromDate, serviceIdNumber, showHistory, toDate]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const loadedInstanceIds = useMemo(
    () => loadedInstances.map((instance) => instance.serviceInstanceId),
    [loadedInstances],
  );

  const selectionIsLoaded = useMemo(() => {
    if (selectedInstanceIds.length !== loadedInstanceIds.length) return false;

    const loadedIds = new Set(loadedInstanceIds);
    return selectedInstanceIds.every((instanceId) => loadedIds.has(instanceId));
  }, [loadedInstanceIds, selectedInstanceIds]);

  const toggleInstance = (instanceId: number) => {
    setSelectedInstanceIds((current) => {
      if (current.includes(instanceId)) {
        return current.filter((selectedId) => selectedId !== instanceId);
      }

      if (current.length >= MAX_SELECTED_SESSIONS) {
        return current;
      }

      return [...current, instanceId];
    });
  };

  const loadSelectedRotas = () => {
    if (selectedInstanceIds.length === 0) return;
    setRotaExportData(null);
    setLoadedInstances(
      instances.filter((instance) =>
        selectedInstanceIds.includes(instance.serviceInstanceId),
      ),
    );
    setRefreshVersion((current) => current + 1);
  };

  const refreshLoadedRotas = () => {
    if (loadedInstanceIds.length === 0) return;
    setRotaExportData(null);
    setRefreshVersion((current) => current + 1);
  };

  const resetRotaSelection = () => {
    setLoadedInstances([]);
    setSelectedInstanceIds([]);
    setRotaExportData(null);
  };

  const activeAssignmentCount = useMemo(
    () =>
      rotaExportData?.reduce(
        (count, rota) =>
          count +
          rota.assignments.filter(
            (assignment) =>
              assignment.status !== "Cancelled" && assignment.status !== "Removed",
          ).length,
        0,
      ) ?? 0,
    [rotaExportData],
  );

  const generateDocument = useCallback(
    () =>
      generateRotaPdf({
        organisationName: selectedTenant?.tenantName,
        serviceName: service?.name ?? "Service",
        location: service?.serviceAddress?.address.fullAddress || undefined,
        generatedBy: user?.displayName,
        rotas: rotaExportData ?? [],
      }),
    [rotaExportData, selectedTenant?.tenantName, service, user?.displayName],
  );

  const rotaFileName = useMemo(
    () =>
      buildDocumentFileName({
        documentType: "rota",
        serviceName: service?.name,
        date: loadedInstances[0]?.startDateTime,
      }),
    [loadedInstances, service?.name],
  );

  return (
    <div className="page-container">
      <PageHeader
        title={service?.name ?? "Service"}
        subtitle="Rota Management"
        actions={
          <button
            type="button"
            className="icon-button-solid"
            title={
              rotaOrigin === "services"
                ? "Back to service management"
                : "Back to dashboard"
            }
            aria-label={
              rotaOrigin === "services"
                ? "Back to service management"
                : "Back to dashboard"
            }
            onClick={handleBack}
          >
            <ArrowBackIcon fontSize="small" />
          </button>
        }
      />

      <section className="rota-workspace">
        {loadedInstances.length === 0 ? (
          <>
            <div className="rota-workspace-heading">
              <div className="rota-workspace-title">
                <h2>{showHistory ? "Rota history" : "Rota overview"}</h2>
              </div>
              <label className="option-item">
                <input
                  type="checkbox"
                  checked={showHistory}
                  onChange={(event) => {
                    const history = event.target.checked;
                    setShowHistory(history);
                    setInstances([]);
                    setSelectedInstanceIds([]);
                    setFromDate(dayjs().subtract(history ? 3 : 0, "month").format("YYYY-MM-DD"));
                    setToDate(dayjs().add(history ? 0 : 30, "day").format("YYYY-MM-DD"));
                  }}
                />
                History
              </label>
            </div>

            <div className="rota-date-range">
              <div className="form-group">
                <label className="form-label">From</label>
                <AppDatePicker
                  value={fromDate}
                  maxDate={dayjs(toDate)}
                  onChange={(value) => setFromDate(value ?? "")}
                />
              </div>
              <div className="form-group">
                <label className="form-label">To</label>
                <AppDatePicker
                  value={toDate}
                  minDate={dayjs(fromDate)}
                  onChange={(value) => setToDate(value ?? "")}
                />
              </div>
            </div>

            <div className="rota-session-selection-heading">
              <div className="rota-selection-count">
                {selectedInstanceIds.length} / {MAX_SELECTED_SESSIONS} selected
              </div>
            </div>

            <div className="rota-session-picker">
              {instances.length === 0 && (
                <div className="field-hint">No sessions in this date range.</div>
              )}

              {instances.map((instance) => {
                const start = new Date(instance.startDateTime);
                const end = new Date(instance.endDateTime);
                const selected = selectedInstanceIds.includes(
                  instance.serviceInstanceId,
                );
                const selectionLimitReached =
                  selectedInstanceIds.length >= MAX_SELECTED_SESSIONS && !selected;

                return (
                  <button
                    key={instance.serviceInstanceId}
                    type="button"
                    className={`service-enrollment-item rota-session-option${
                      selected ? " rota-session-option--selected" : ""
                    }`}
                    onClick={() => toggleInstance(instance.serviceInstanceId)}
                    disabled={selectionLimitReached}
                    aria-pressed={selected}
                  >
                    <div>
                      <div className="service-enrollment-name">
                        {dateFormatter.format(start)}
                      </div>
                      <div className="field-hint" style={{ marginBottom: 0 }}>
                        {timeFormatter.format(start)} - {timeFormatter.format(end)}
                      </div>
                    </div>
                    {selected && <EventAvailableIcon fontSize="small" />}
                  </button>
                );
              })}
            </div>

            <div className="rota-load-toolbar">
              <div className="rota-load-guidance">
                {selectedInstanceIds.length === 0
                  ? "Select up to five sessions, then load their rotas."
                  : `${selectedInstanceIds.length} session${selectedInstanceIds.length === 1 ? "" : "s"} ready to load.`}
              </div>
              <div className="rota-load-actions">
                <button
                  type="button"
                  className="action-button primary rota-load-button"
                  onClick={loadSelectedRotas}
                  disabled={selectedInstanceIds.length === 0 || selectionIsLoaded}
                >
                  <ViewWeekIcon fontSize="small" />
                  Load rotas
                  {selectedInstanceIds.length > 0
                    ? ` (${selectedInstanceIds.length})`
                    : ""}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="rota-loaded-toolbar">
            <button
              type="button"
              className="action-button secondary rota-change-sessions-button"
              onClick={resetRotaSelection}
            >
              <ViewWeekIcon fontSize="small" />
              Change sessions
            </button>

            <div className="rota-loaded-actions">
              <button
                type="button"
                className="action-button secondary rota-refresh-button"
                onClick={refreshLoadedRotas}
              >
                <RefreshIcon fontSize="small" />
                <span>Refresh</span>
              </button>
              <DocumentActions
                documentType="rota-summary"
                title={`${service?.name ?? "Service"} rota`}
                fileName={rotaFileName}
                generateDocument={generateDocument}
                shareTitle={`${service?.name ?? "Service"} volunteer rota`}
                shareText="Volunteer rota from FrontLine."
                disabled={!rotaExportData || activeAssignmentCount === 0}
                disabledMessage="No assignments to export."
                compact
              />
            </div>
          </div>
        )}

        {loadedInstances.length > 0 && (
          <MultiServiceInstanceRotaBuilder
            key={refreshVersion}
            instances={loadedInstances}
            onExportDataChange={setRotaExportData}
          />
        )}
      </section>
    </div>
  );
}
